import React from "react";
import { Box, Card, CardContent, Typography, IconButton } from "@mui/material";
import { ArrowBackIosNew, ArrowForwardIos } from "@mui/icons-material";
import FavoriteIcon from '@mui/icons-material/Favorite';
import { Swiper, SwiperSlide } from "swiper/react";
import { Navigation, Autoplay } from "swiper/modules";
import "swiper/css";
import "swiper/css/navigation";

export default function SuccessStories({ config }) {
  if (!config || config.length === 0) return null;

  return (
    <Box sx={{ py: 8, px: { xs: 2, md: 6 }, background: "#FAF6F2", position: "relative" }}>
      {/* Decorative top border wave or element */}
      <Box
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: "6px",
          background: "linear-gradient(90deg, #BF0603 0%, #D4AF37 50%, #BF0603 100%)" // Gold-crimson gradient line
        }}
      />

      {/* Header */}
      <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", mb: 6 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <FavoriteIcon sx={{ color: "#BF0603", fontSize: 20 }} />
          <Typography
            variant="subtitle2"
            sx={{
              fontWeight: 700,
              color: "#BF0603",
              fontFamily: "Quicksand",
              letterSpacing: 2,
              textTransform: "uppercase"
            }}
          >
            Real Matches, Real Love
          </Typography>
          <FavoriteIcon sx={{ color: "#BF0603", fontSize: 20 }} />
        </Box>
        
        <Typography
          variant="h4"
          sx={{
            fontWeight: 800,
            color: "#111",
            fontFamily: "Quicksand",
            fontSize: { xs: "26px", md: "36px" },
            textAlign: "center"
          }}
        >
          Our Success Stories
        </Typography>

        <Typography
          variant="body1"
          sx={{
            color: "#666",
            fontFamily: "Varela Round",
            textAlign: "center",
            maxWidth: "600px",
            mt: 1.5,
            fontSize: "0.95rem"
          }}
        >
          Read the heartwarming journeys of couples who found their perfect lifelong companion through Vivaha Sangamam.
        </Typography>
      </Box>

      {/* Swiper Carousel */}
      <Box sx={{ position: "relative", px: { xs: 0, md: 4 } }}>
        {/* Custom Arrow Buttons */}
        <IconButton
          className="swiper-button-prev"
          sx={{
            position: "absolute",
            top: "50%",
            left: "-15px",
            transform: "translateY(-50%)",
            zIndex: 10,
            backgroundColor: "#fff",
            boxShadow: "0 6px 15px rgba(0,0,0,0.1)",
            width: 42,
            height: 42,
            border: "1px solid rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": { 
              backgroundColor: "#BF0603",
              "& svg": { color: "#fff" }
            },
            display: { xs: "none", md: "flex" }
          }}
        >
          <ArrowBackIosNew sx={{ color: "#BF0603", fontSize: 18, ml: -0.3 }} />
        </IconButton>

        <IconButton
          className="swiper-button-next"
          sx={{
            position: "absolute",
            top: "50%",
            right: "-15px",
            transform: "translateY(-50%)",
            zIndex: 10,
            backgroundColor: "#fff",
            boxShadow: "0 6px 15px rgba(0,0,0,0.1)",
            width: 42,
            height: 42,
            border: "1px solid rgba(0,0,0,0.05)",
            transition: "all 0.3s ease",
            "&:hover": { 
              backgroundColor: "#BF0603",
              "& svg": { color: "#fff" }
            },
            display: { xs: "none", md: "flex" }
          }}
        >
          <ArrowForwardIos sx={{ color: "#BF0603", fontSize: 18, mr: -0.3 }} />
        </IconButton>

        <Swiper
          modules={[Navigation, Autoplay]}
          spaceBetween={30}
          slidesPerView={3}
          navigation={{ nextEl: ".swiper-button-next", prevEl: ".swiper-button-prev" }}
          autoplay={{ delay: 3500, disableOnInteraction: false }}
          loop
          breakpoints={{
            1200: { slidesPerView: 3 },
            850: { slidesPerView: 2 },
            600: { slidesPerView: 2 },
            0: { slidesPerView: 1 },
          }}
          style={{ paddingBottom: "50px", paddingTop: "10px" }}
        >
          {config.map((item) => (
            <SwiperSlide key={item.id}>
              <Card
                sx={{
                  borderRadius: "20px",
                  overflow: "hidden",
                  boxShadow: "0 10px 30px rgba(191, 6, 3, 0.03)",
                  background: "#fff",
                  maxWidth: 380,
                  mx: "auto",
                  height: "100%",
                  display: "flex",
                  flexDirection: "column",
                  border: "1px solid rgba(212, 175, 55, 0.15)", // Gold border accent
                  transition: "all 0.4s ease",
                  "&:hover": {
                    transform: "translateY(-6px)",
                    boxShadow: "0 15px 35px rgba(191, 6, 3, 0.08)",
                    border: "1px solid rgba(212, 175, 55, 0.4)"
                  }
                }}
              >
                {/* Couple Image Wrapper */}
                <Box
                  sx={{
                    width: "100%",
                    height: 250,
                    overflow: "hidden",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    position: "relative",
                    backgroundColor: "#f5f5f5"
                  }}
                >
                  <img
                    src={item.img}
                    alt={item.title}
                    style={{
                      width: "100%",
                      height: "100%",
                      objectFit: "cover",
                    }}
                  />
                  {/* Verified Badge */}
                  <Box
                    sx={{
                      position: "absolute",
                      bottom: 12,
                      right: 12,
                      backgroundColor: "rgba(255, 255, 255, 0.9)",
                      backdropFilter: "blur(5px)",
                      border: "1px solid #D4AF37",
                      borderRadius: "20px",
                      px: 1.5,
                      py: 0.5,
                      display: "flex",
                      alignItems: "center",
                      gap: 0.5
                    }}
                  >
                    <FavoriteIcon sx={{ color: "#BF0603", fontSize: 14 }} />
                    <Typography
                      variant="caption"
                      sx={{
                        fontWeight: 700,
                        color: "#222",
                        fontFamily: "Quicksand",
                        fontSize: "0.75rem"
                      }}
                    >
                      Verified Match
                    </Typography>
                  </Box>
                </Box>

                {/* Testimonial Content */}
                <CardContent
                  sx={{
                    flexGrow: 1,
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    p: 4,
                    position: "relative"
                  }}
                >
                  {/* Quote Icon Background */}
                  <Typography
                    sx={{
                      position: "absolute",
                      top: 10,
                      left: 20,
                      fontSize: "6rem",
                      fontFamily: "serif",
                      color: "rgba(191, 6, 3, 0.04)",
                      lineHeight: 0,
                      userSelect: "none"
                    }}
                  >
                    “
                  </Typography>

                  <Typography
                    variant="h6"
                    sx={{
                      fontWeight: 700,
                      color: "#111",
                      fontFamily: "Quicksand",
                      fontSize: "1.1rem",
                      mb: 1.5,
                      textAlign: "center"
                    }}
                  >
                    {item.title}
                  </Typography>

                  <Typography
                    sx={{
                      textAlign: "center",
                      color: "#555",
                      fontSize: "0.92rem",
                      lineHeight: "1.6",
                      fontStyle: "italic",
                      fontFamily: "Varela Round",
                      mb: 2
                    }}
                  >
                    "{item.story}"
                  </Typography>

                  {/* Decorative separator */}
                  <Box
                    sx={{
                      width: "30px",
                      height: "2px",
                      backgroundColor: "#D4AF37",
                      mx: "auto",
                      mt: 1
                    }}
                  />
                </CardContent>
              </Card>
            </SwiperSlide>
          ))}
        </Swiper>
      </Box>
    </Box>
  );
}
